import React from 'react'
import { Info, CheckCircle, AlertTriangle, XCircle, X } from 'lucide-react'

export interface AlertProps {
  variant?: 'info' | 'success' | 'warning' | 'error'
  message: string
  onClose?: () => void
  className?: string
  autoClose?: boolean
  duration?: number
}

const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  message,
  onClose,
  className = '',
  autoClose = false,
  duration = 5000
}) => {
  React.useEffect(() => {
    if (autoClose && onClose) {
      const timer = setTimeout(() => {
        onClose()
      }, duration)
      return () => clearTimeout(timer)
    }
  }, [autoClose, duration, onClose])

  const variantConfig = {
    info: {
      background: 'bg-[#E2F0FB]',
      iconColor: 'text-[#256EF4]',
      Icon: Info
    },
    success: {
      background: 'bg-[#E8F5E8]',
      iconColor: 'text-[#4CAF50]',
      Icon: CheckCircle
    },
    warning: {
      background: 'bg-[#FFF4E6]',
      iconColor: 'text-[#FF9800]',
      Icon: AlertTriangle
    },
    error: {
      background: 'bg-[#FFEBEE]',
      iconColor: 'text-[#F44336]',
      Icon: XCircle
    }
  }

  const config = variantConfig[variant]
  const IconComponent = config.Icon

  return (
    <div 
      className={`
        flex items-center gap-[10px] p-[10px] w-[500px] h-[70px]
        ${config.background} border border-[#EBEBEB] rounded-lg
        ${className}
      `}
    >
      {/* Icon Section */}
      <div className="flex items-center gap-[6px] px-[10px] flex-1">
        <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
          <IconComponent 
            className={`w-[26.67px] h-[26.67px] ${config.iconColor}`}
            strokeWidth={2}
          />
        </div>
        
        {/* Message Section */}
        <div className="flex items-center p-[10px]">
          <span 
            className="text-[#000000] text-xl font-normal"
            style={{ 
              fontFamily: 'Pretendard GOV', 
              lineHeight: '1.193359375em' 
            }}
          >
            {message}
          </span>
        </div>
      </div>

      {/* Close Button Section */}
      {onClose && (
        <div className="flex items-center justify-center p-[10px]">
          <button
            onClick={onClose}
            className="w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors"
            aria-label="알림 닫기"
          >
            <X 
              className="w-4 h-4 text-[#4A4B4C]" 
              strokeWidth={2}
            />
          </button>
        </div>
      )}
    </div>
  )
}

export default Alert